import { useEffect } from 'react';

import { motion } from 'framer-motion';
import styled from 'styled-components';

import { Button } from '@components/button';
import { IconCheckboxChecked } from '@components/icons/checkbox-checked';
import { IconFormIndicatorError } from '@components/icons/form-indicator-error';
import { IconFormIndicatorInfo } from '@components/icons/form-indicator-info';
import { IconOpen } from '@components/icons/open';
import { IconOpenNewTab } from '@components/icons/open-new-tab';
import { IconRedo } from '@components/icons/redo';
import { IconRestart } from '@components/icons/restart';
import { SlidingProgressBar } from '@components/progress-bar/progress-bar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import type { Toast as ToastType } from './use-toaster';
import { isToastError, remove } from './use-toaster';

type ToastProps = { toast: ToastType; index: number };

const OpenUrlButtons = ({ url }: Pick<ToastType, 'url'>) => {
  const { t } = useScopedTranslate(translations);

  return (
    <Stack space={8} orientation="horizontal">
      <Button href={url} variant="ghost">
        <Stack space={2} align="center" orientation="horizontal">
          <IconOpen />
          <Text font="semibold14" color="textSecondary" text={t('open')} />
        </Stack>
      </Button>
      <Button href={url} target="_blank" variant="ghost">
        <Stack space={2} align="center" orientation="horizontal">
          <IconOpenNewTab />
          <Text font="semibold14" color="textSecondary" text={t('openInNewTab')} />
        </Stack>
      </Button>
    </Stack>
  );
};

const RetryAndResolveButtons = ({ toast }: { toast: ToastType }) => {
  const { t } = useScopedTranslate(translations);

  if (!isToastError(toast)) {
    return null;
  }

  return (
    <Stack space={8} orientation="horizontal">
      <Button variant="ghost" onClick={toast.resolve}>
        <Stack space={2} align="center" orientation="horizontal">
          <IconRestart />
          <Text font="semibold14" color="textSecondary" text={t('resolveIssue')} />
        </Stack>
      </Button>
      <Button variant="ghost" onClick={toast.retry}>
        <Stack space={2} align="center" orientation="horizontal">
          <IconRedo />
          <Text font="semibold14" color="textSecondary" text={t('retry')} />
        </Stack>
      </Button>
    </Stack>
  );
};

const CustomButtons = ({ toast }: { toast: ToastType }) => {
  if (!toast.customButtons || toast.customButtons.length === 0) {
    return null;
  }

  return (
    <Stack space={2} orientation="horizontal">
      {toast.customButtons.map((button, index) => (
        <Button variant="secondary" onClick={button.onClick} key={index}>
          <Stack space={1} align="center" orientation="horizontal">
            {button.icon}
            <Text font="semibold14" color="textSecondary" text={button.text} />
          </Stack>
        </Button>
      ))}
    </Stack>
  );
};

const variantIconMap = {
  info: <IconFormIndicatorInfo color="elementIndigo100" />,
  error: <IconFormIndicatorError color="elementRed100" />,
  success: <IconCheckboxChecked color="elementGreen100" />,
};

export const Toast = ({ toast, index }: ToastProps) => {
  const icon = variantIconMap[toast.variant];

  useEffect(() => {
    if (toast.duration === 0) return;
    if (!toast.customButtons?.length && !toast.url) {
      const timer = setTimeout(() => remove(toast.id), (toast.duration ?? 5.5) * 1000);
      return () => clearTimeout(timer);
    }
  }, [toast.customButtons, toast.url, toast.id, toast.duration]);

  return (
    <ToastMessage
      initial={{ opacity: 0, top: '2rem' }}
      animate={{ opacity: 1, top: 0, transition: { duration: 0.3 } }}
      exit={{ opacity: 0, top: '2rem', transition: { duration: 0.3 } }}
      data-cy="toast"
      onClick={() => remove(toast.id)}
      style={{
        zIndex: 100 + index,
      }}
    >
      <Stack space={4}>
        <Stack space={4} orientation="horizontal">
          {icon}
          <div>
            <Text text={toast.title} color="textPrimary" font="regular14" />
            {toast.subtitle && (
              <Text text={toast.subtitle} color="textSecondary" font="regular14" />
            )}
          </div>
        </Stack>
        <RetryAndResolveButtons toast={toast} />
        <CustomButtons toast={toast} />
        {toast.url && <OpenUrlButtons url={toast.url} />}
        {((toast.customButtons && toast.customButtons?.length > 0) || toast.url) &&
          toast.duration !== 0 && (
            <SlidingProgressBar
              variant={toast.variant}
              duration={toast.duration}
              onDone={() => remove(toast.id)}
            />
          )}
      </Stack>
    </ToastMessage>
  );
};

const ToastMessage = styled(motion.div)({
  position: 'relative',
  backgroundColor: theme.color.backgroundLightDark,
  overflow: 'hidden',
  cursor: 'pointer',
  borderRadius: pxToRem(8),
  padding: theme.space[8],
  boxShadow: theme.shadow.fancy,
  font: theme.font.bold16,
  width: pxToRem(424),
});

const translations = {
  en: {
    retry: 'Retry',
    resolveIssue: 'Resolve issue',
    open: 'Open',
    openInNewTab: 'Open in new tab',
  },
};
